import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PiQuestionFill, PiQuestion } from 'react-icons/pi'
import { LeftNavIconButton } from './LeftNavIconButton'
import { useLeftNavStore } from './left-nav-store'
import {
  isNavItemEnabled,
  hasSlideOut,
  getActiveNavSection,
  resolvePath,
} from './useNavItems'
import { useNavItems } from './NavItemsContext'
import { ProfileMenu } from './ProfileMenu'
import { HelpMenu } from './HelpMenu'
import { CompanyAvatar } from './CompanyAvatar'
import { useAuth } from 'context/authentication/useAuth'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { Z_INDEX } from 'styles/z-index'
import { Popover, PopoverAnchor, PopoverContent } from 'components/UI/popover'
import type { NavItemConfig, NavSection } from './types'
import { cn } from 'components/UI/cn'

const SUPPORT_EMAIL = 'support@firstbite.io'

function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const LeftNavBar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout, isAllowed, user } = useAuth()
  const featureFlags = useFeatureFlagService()
  const { navItems } = useNavItems()

  const {
    isCollapsed,
    activeSection,
    isSlideOutOpen,
    setActiveSection,
    openSlideOut,
    closeSlideOut,
    lastVisited,
    setLastVisited,
  } = useLeftNavStore()

  // Filter nav items based on feature flags and user type.
  // Settings lives in the top nav header now — keep it in navItems for
  // breadcrumb resolution but hide it from the left nav.
  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (item.id === 'settings') return false
      if (!isNavItemEnabled(item, featureFlags, user)) return false
      if (!isAllowed(item.minimumRole ?? 'Standard')) return false
      return true
    })
  }, [navItems, featureFlags, user, isAllowed])

  // Determine active section from current path
  const currentActiveSection = useMemo(
    (): NavSection | null => getActiveNavSection(location.pathname, navItems),
    [location.pathname, navItems]
  )

  /**
   * Navigation State Management (FIR-4955)
   *
   * Automatically tracks the user's last visited page within each navigation section.
   * This enables "memory" behavior where returning to a section restores the previous
   * page instead of resetting to the default.
   *
   * Example:
   * - User navigates to Targets > Commercial > Hotels
   * - User switches to Deals section
   * - User clicks Targets again → returns to Commercial > Hotels (not default Restaurants & Bars)
   *
   * State is persisted to localStorage via Zustand middleware for cross-session memory.
   */
  React.useEffect(() => {
    if (currentActiveSection) {
      setLastVisited(currentActiveSection, location.pathname)
    }
  }, [currentActiveSection, location.pathname, setLastVisited])

  /**
   * Handles navigation item clicks with intelligent routing (FIR-4955)
   *
   * Behavior:
   * 1. For sections WITH slide-out panels (Targets, Performance, Workspace, Settings):
   *    - First click: Opens panel AND navigates to last visited or default page
   *    - Subsequent clicks while open: Toggles panel closed
   *
   * 2. For sections WITHOUT slide-out panels (Deals, Campaigns, Samples, Rebates):
   *    - First click: Navigates to last visited page within this section
   *    - Second click (already active): Navigates back to the section's list page
   *    - Closes any open panels
   *
   * Navigation Priority:
   * - lastVisited[section] (user's previous page in this section)
   * - item.path (default page defined in NAV_ITEMS)
   */
  const handleNavClick = useCallback(
    (item: NavItemConfig) => {
      const isCurrentlyActive = currentActiveSection === item.id
      const itemHasSlideOut = hasSlideOut(item)

      if (itemHasSlideOut) {
        // For sections with slide-out panels
        if (isCurrentlyActive && isSlideOutOpen) {
          // Close slide-out if clicking the already active section with panel open
          closeSlideOut()
        } else {
          // Open slide-out AND navigate to last visited or default page
          openSlideOut(item.id)
          navigate(lastVisited[item.id] || resolvePath(item.path))
        }
      } else {
        // For sections without slide-out panels, navigate directly
        setActiveSection(item.id)
        closeSlideOut()
        // If already in this section, go back to the list page (item.path);
        // otherwise restore the last visited page within this section.
        if (isCurrentlyActive) {
          navigate(resolvePath(item.path))
        } else {
          navigate(lastVisited[item.id] || resolvePath(item.path))
        }
      }
    },
    [
      currentActiveSection,
      isSlideOutOpen,
      setActiveSection,
      openSlideOut,
      closeSlideOut,
      navigate,
      lastVisited,
    ]
  )

  const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false)
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false)

  const helpMenuItems = useMemo(
    () =>
      [
        { label: 'Help Center', url: 'https://help.firstbite.io/' },
        { label: 'Contact Us', url: `mailto:${SUPPORT_EMAIL}` },
        { label: 'Privacy Policy', url: '/privacy-policy' },
        { label: 'Terms of Use', url: '/terms-of-use' },
      ].map(({ label, url }) => ({
        label,
        onClick: () => {
          if (url.startsWith('/')) {
            navigate(url)
          } else {
            openExternal(url)
          }
          setIsHelpMenuOpen(false)
        },
      })),
    [navigate]
  )

  const handleLogoutClick = useCallback(() => {
    setIsProfileMenuOpen(false)
    logout()
  }, [logout])

  return (
    <nav
      className={cn(
        'flex-col w-[var(--left-nav-width)] min-w-[var(--left-nav-width)] h-full bg-[var(--nav-bg-primary)] px-3 pb-6',
        {
          hidden: isCollapsed,
          flex: !isCollapsed,
        }
      )}
      style={{ zIndex: Z_INDEX.LEFT_NAV, width: 'var(--left-nav-width)' }}
      aria-label="Main navigation"
      data-left-nav
    >
      <div className="flex flex-col flex-1 items-center min-h-0">
        {/* Scrolls independently so the pinned bottom section stays reachable on short viewports */}
        <div className="flex flex-col items-center gap-3 pt-1 flex-1 min-h-0 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {visibleNavItems.map((item) => (
            <LeftNavIconButton
              key={item.id}
              icon={item.icon}
              outlineIcon={item.outlineIcon}
              label={item.label}
              isSelected={currentActiveSection === item.id}
              isSecondaryActive={
                hasSlideOut(item) && activeSection === item.id && isSlideOutOpen
              }
              onClick={() => handleNavClick(item)}
              showLabel={true}
            />
          ))}
        </div>

        {/* Bottom Section — pinned, never scrolls or clips */}
        <div className="flex flex-col items-center gap-2 shrink-0">
          {/* Divider sits above both icon buttons */}
          <div className="w-11 h-px bg-[var(--nav-text-primary)] opacity-50 my-2" />

          <Popover open={isHelpMenuOpen} onOpenChange={setIsHelpMenuOpen}>
            <PopoverAnchor>
              <LeftNavIconButton
                icon={<PiQuestionFill size={24} />}
                outlineIcon={<PiQuestion size={24} />}
                label="Help"
                isSelected={isHelpMenuOpen}
                suppressTooltip={isHelpMenuOpen}
                onClick={() => setIsHelpMenuOpen((open) => !open)}
              />
            </PopoverAnchor>
            <PopoverContent
              side="right"
              align="end"
              sideOffset={8}
              style={{ width: 'fit-content' }}
              className="p-0 rounded-xl border-line w-auto"
            >
              <HelpMenu items={helpMenuItems} />
            </PopoverContent>
          </Popover>

          <Popover open={isProfileMenuOpen} onOpenChange={setIsProfileMenuOpen}>
            <PopoverAnchor>
              <LeftNavIconButton
                icon={<CompanyAvatar />}
                label={user?.email ?? 'Profile'}
                isSelected={isProfileMenuOpen}
                suppressTooltip={isProfileMenuOpen}
                onClick={() => setIsProfileMenuOpen((open) => !open)}
              />
            </PopoverAnchor>
            <PopoverContent
              side="right"
              align="end"
              sideOffset={8}
              style={{ width: 'fit-content' }}
              className="p-0 rounded-xl border-line w-auto"
            >
              <ProfileMenu onLogout={handleLogoutClick} />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </nav>
  )
}
